/**
 * Save/Load System for Age of Claudes v2
 *
 * Serializes game state to localStorage and restores it.
 */

import { Game, GameConfig, Resources, GamePhase } from './Game'
import { Unit } from './Unit'
import { Building } from './Building'

// ============ Save Data Types ============

export interface SavedUnit {
  typeId: string
  x: number
  y: number
  isPlayer: boolean
  hp: number
  state: string
  carrying: number
  carryType: string | null
  gatherTarget?: { x: number; y: number }
  path?: { x: number; y: number }[]
}

export interface SavedTrainingItem {
  unitType: string
  progress: number
  totalTime: number
}

export interface SavedBuilding {
  typeId: string
  tileX: number
  tileY: number
  isPlayer: boolean
  hp: number
  constructionProgress: number
  trainingQueue: SavedTrainingItem[]
  rallyPoint?: { x: number; y: number }
}

export interface SavedGameState {
  version: number
  timestamp: number
  config: GameConfig
  phase: GamePhase
  resources: Resources
  enemyResources: Resources
  population: number
  populationCap: number
  camera: {
    x: number
    y: number
    zoom: number
  }
  units: SavedUnit[]
  buildings: SavedBuilding[]
  enemyAI: {
    aiTimer: number
    attackTimer: number
  }
}

const SAVE_VERSION = 1
const SAVE_KEY = 'age-of-claudes-v2-save'
const SAVE_SLOTS_KEY = 'age-of-claudes-v2-save-slots'

// ============ Save Functions ============

export function serializeUnit(unit: Unit): SavedUnit {
  const unitAny = unit as any  // Access private fields

  return {
    typeId: unit.unitType.id,
    x: unit.x,
    y: unit.y,
    isPlayer: unit.isPlayer,
    hp: unit.hp,
    state: unit.state,
    carrying: unit.carrying,
    carryType: unit.carryType,
    gatherTarget: unitAny.gatherTarget ? { ...unitAny.gatherTarget } : undefined,
    path: unitAny.path?.length > 0 ? unitAny.path.map((p: { x: number; y: number }) => ({ ...p })) : undefined
  }
}

export function serializeBuilding(building: Building): SavedBuilding {
  return {
    typeId: building.buildingType.id,
    tileX: building.tileX,
    tileY: building.tileY,
    isPlayer: building.isPlayer,
    hp: building.hp,
    constructionProgress: building.constructionProgress,
    trainingQueue: building.trainingQueue.map(item => ({
      unitType: item.unitType,
      progress: item.progress,
      totalTime: item.totalTime
    })),
    rallyPoint: building.rallyPoint ? { x: building.rallyPoint.x, y: building.rallyPoint.y } : undefined
  }
}

export function serializeGame(game: Game): SavedGameState {
  // Access private enemy AI state via any cast (necessary for save/load)
  const gameAny = game as any

  return {
    version: SAVE_VERSION,
    timestamp: Date.now(),
    config: { ...game.config },
    phase: game.phase,
    resources: { ...game.resources },
    enemyResources: { ...gameAny.enemyResources },
    population: game.population,
    populationCap: game.populationCap,
    camera: {
      x: game.renderer.camera.x,
      y: game.renderer.camera.y,
      zoom: game.renderer.camera.zoom
    },
    units: [
      ...game.playerUnits.map(serializeUnit),
      ...game.enemyUnits.map(serializeUnit)
    ],
    buildings: [
      ...game.playerBuildings.map(serializeBuilding),
      ...game.enemyBuildings.map(serializeBuilding)
    ],
    enemyAI: {
      aiTimer: gameAny.enemyAITimer || 0,
      attackTimer: gameAny.enemyAttackTimer || 0
    }
  }
}

// ============ Save to Storage ============

export function saveGame(game: Game, slotName: string = 'quicksave'): boolean {
  try {
    const saveData = serializeGame(game)
    const saveJson = JSON.stringify(saveData)

    // Save to slot
    localStorage.setItem(`${SAVE_KEY}-${slotName}`, saveJson)

    // Update slot list
    const slots = getSaveSlots()
    if (!slots.includes(slotName)) {
      slots.push(slotName)
      localStorage.setItem(SAVE_SLOTS_KEY, JSON.stringify(slots))
    }

    console.log(`Game saved to slot: ${slotName}`)
    return true
  } catch (error) {
    console.error('Failed to save game:', error)
    return false
  }
}

export function loadSaveData(slotName: string = 'quicksave'): SavedGameState | null {
  try {
    const saveJson = localStorage.getItem(`${SAVE_KEY}-${slotName}`)
    if (!saveJson) {
      console.log(`No save found in slot: ${slotName}`)
      return null
    }

    const saveData: SavedGameState = JSON.parse(saveJson)

    // Version check - could add migration logic here
    if (saveData.version !== SAVE_VERSION) {
      console.warn(`Save version mismatch: ${saveData.version} vs ${SAVE_VERSION}`)
      // For now, attempt to load anyway
    }

    return saveData
  } catch (error) {
    console.error('Failed to load save:', error)
    return null
  }
}

export function getSaveSlots(): string[] {
  try {
    const slotsJson = localStorage.getItem(SAVE_SLOTS_KEY)
    return slotsJson ? JSON.parse(slotsJson) : []
  } catch {
    return []
  }
}

export function deleteSave(slotName: string): boolean {
  try {
    localStorage.removeItem(`${SAVE_KEY}-${slotName}`)

    // Update slot list
    const slots = getSaveSlots().filter(s => s !== slotName)
    localStorage.setItem(SAVE_SLOTS_KEY, JSON.stringify(slots))

    console.log(`Deleted save slot: ${slotName}`)
    return true
  } catch (error) {
    console.error('Failed to delete save:', error)
    return false
  }
}

export function getSaveInfo(slotName: string): { timestamp: number; phase: string } | null {
  try {
    const saveJson = localStorage.getItem(`${SAVE_KEY}-${slotName}`)
    if (!saveJson) return null

    const saveData: SavedGameState = JSON.parse(saveJson)
    return {
      timestamp: saveData.timestamp,
      phase: saveData.phase
    }
  } catch {
    return null
  }
}

// ============ Restore Game State ============

/**
 * Restores game state from save data.
 * This should be called on a fresh Game instance after creation.
 */
export function restoreGameState(game: Game, saveData: SavedGameState): void {
  // Clear existing entities (Game.start() may have created initial units)
  for (const unit of [...game.playerUnits, ...game.enemyUnits]) {
    unit.destroy()
  }
  for (const building of [...game.playerBuildings, ...game.enemyBuildings]) {
    // Free tiles before destroying
    game.map.freeTiles(building.tileX, building.tileY, building.buildingType.size)
    building.destroy()
  }

  game.playerUnits = []
  game.enemyUnits = []
  game.playerBuildings = []
  game.enemyBuildings = []

  // Restore resources
  game.resources = { ...saveData.resources }
  game.onResourceChange?.(game.resources)

  // Restore population
  game.population = 0  // Will be incremented as units are restored
  game.populationCap = 0  // Will be set from buildings

  // Restore buildings first (they provide population cap)
  for (const savedBuilding of saveData.buildings) {
    const building = game.createBuilding(
      savedBuilding.typeId,
      savedBuilding.tileX,
      savedBuilding.tileY,
      savedBuilding.isPlayer,
      savedBuilding.constructionProgress >= 100  // startConstructed
    )

    // Restore hp
    building.hp = savedBuilding.hp

    // Restore construction progress if not complete
    if (savedBuilding.constructionProgress < 100) {
      building.constructionProgress = savedBuilding.constructionProgress
    }

    // Restore training queue
    for (const savedItem of savedBuilding.trainingQueue) {
      building.trainingQueue.push({
        unitType: savedItem.unitType,
        progress: savedItem.progress,
        totalTime: savedItem.totalTime
      })
    }

    // Restore rally point
    if (savedBuilding.rallyPoint) {
      building.rallyPoint = { ...savedBuilding.rallyPoint }
    }
  }

  // Restore units
  for (const savedUnit of saveData.units) {
    const unit = game.createUnit(
      savedUnit.typeId,
      savedUnit.x,
      savedUnit.y,
      savedUnit.isPlayer
    )

    // Restore hp
    unit.hp = savedUnit.hp

    // Restore carrying state
    unit.carrying = savedUnit.carrying
    unit.carryType = savedUnit.carryType as 'compute' | 'data' | 'energy' | null

    // Restore movement path if it existed
    if (savedUnit.path && savedUnit.path.length > 0) {
      unit.moveTo(savedUnit.path)
    } else if (savedUnit.gatherTarget && savedUnit.carryType) {
      // Restore gathering
      unit.gatherFrom(savedUnit.gatherTarget.x, savedUnit.gatherTarget.y, savedUnit.carryType as 'compute' | 'data' | 'energy')
    } else if (savedUnit.state === 'returning') {
      // Was returning resources - set state directly
      const unitAny = unit as any
      unitAny.state = 'returning'
    }
  }

  // Restore camera
  game.renderer.setCameraPosition(saveData.camera.x, saveData.camera.y, true)
  game.renderer.setCameraZoom(saveData.camera.zoom, true)

  // Restore enemy AI state
  const gameAny = game as any
  gameAny.enemyResources = { ...saveData.enemyResources }
  gameAny.enemyAITimer = saveData.enemyAI.aiTimer
  gameAny.enemyAttackTimer = saveData.enemyAI.attackTimer

  // Restore game phase
  game.phase = saveData.phase
  game.onPhaseChange?.(game.phase)

  // Trigger UI updates
  game.onPopulationChange?.(game.population, game.populationCap)

  console.log('Game state restored')
}

// ============ Auto-save ============

let autoSaveInterval: number | null = null

export function startAutoSave(game: Game, intervalMs: number = 60000): void {
  stopAutoSave()

  autoSaveInterval = window.setInterval(() => {
    if (game.phase === 'playing') {
      saveGame(game, 'autosave')
    }
  }, intervalMs)

  console.log(`Auto-save enabled (every ${intervalMs / 1000}s)`)
}

export function stopAutoSave(): void {
  if (autoSaveInterval !== null) {
    clearInterval(autoSaveInterval)
    autoSaveInterval = null
  }
}

// ============ Quick Save/Load Helpers ============

export function quickSave(game: Game): boolean {
  return saveGame(game, 'quicksave')
}

export function quickLoad(): SavedGameState | null {
  return loadSaveData('quicksave')
}

export function hasQuickSave(): boolean {
  return localStorage.getItem(`${SAVE_KEY}-quicksave`) !== null
}

export function hasAutoSave(): boolean {
  return localStorage.getItem(`${SAVE_KEY}-autosave`) !== null
}
